/**
 * THE COMPOSER — where the Owner types, pastes, and attaches.
 *
 * THE BOUND IS THE SERVER'S AND IT IS SHOWN, NOT ENFORCED SILENTLY. The question
 * bound is a transport runaway, not a scientific length rule (H-032). What this
 * control refuses to do is TRUNCATE: it never shortens what the Owner supplied.
 *
 * TWO KINDS OF LONG INPUT, AND THEY ARE KEPT APART:
 *
 *   PASTED TEXT that is long collapses into a `[Pasted text — N lines]` chip,
 *   exactly because putting a 50,000-character log into the textarea makes the
 *   conversation row and every later prompt carry it. The whole text is filed
 *   as a material, the turn carries a reference, and the chip can be expanded,
 *   edited or removed before sending. The content the Owner pasted is still
 *   there — a preview is presentation, never truncation.
 *
 *   FILES are uploaded to the same material store. The original bytes are kept
 *   (so a download returns them) and the server extracts a reading, stating
 *   plainly when it could not read a file. The chip shows the filename, type,
 *   size and the extraction status; an image gets a thumbnail.
 *
 * NOTHING IS CLEARED WHEN SEND IS PRESSED. The draft stays in the box until the
 * parent says the turn was accepted (`clearDraftNonce`). A failed send
 * therefore leaves every word and every attachment exactly where it was, and a
 * reload restores the same draft from local storage. The store — not local
 * storage — holds the material; the local copy is only the reference list.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import {
	formatBytes,
	ingestMaterial,
	loadMaterialBlob,
	readMaterialText,
	uploadMaterialFile,
	type MaterialSummary,
} from './chat';

/** The server's bound, restated here so the control can show it before a
 *  request is made rather than after one is refused. The bound is a transport
 *  runaway, not a question-length rule (H-032); a 5,000-character question is
 *  a real question and is stored whole. */
export const QUESTION_MAX_CHARS = 200_000;

/** WHEN A PASTE BECOMES A CHIP. The numbers are a presentation threshold, not
 *  a storage rule: below them the text is a message the Owner is writing;
 *  above them it is a document being handed over, and filing it keeps the
 *  conversation readable without losing a character. */
export const PASTE_COLLAPSE_CHARS = 6_000;
export const PASTE_COLLAPSE_LINES = 80;

/** The server's per-turn material bound, restated so the control can refuse
 *  before a paid model call is started. */
export const MATERIAL_MAX_REFS_PER_TURN = 10;

/** How much of a material the expandable preview renders. The STORE keeps
 *  everything; this bound exists because a 10 MB `<pre>` freezes a phone. The
 *  preview says so, and Download returns the original. */
const PREVIEW_CHARS = 100_000;


export interface AttachedMaterial {
	materialId: string;
	title: string;
	lineCount: number;
	byteSize: number;
	/** Present on a file; absent on a pasted material. */
	fileName?: string;
	mediaType?: string;
	extractionStatus?: string;
	extractionNote?: string;
	/** How the draft got it. A pasted material may be edited; an uploaded file
	 *  is the Owner's original and is not rewritten by this control. */
	origin?: 'PASTE' | 'FILE';
	/** The pasted text, kept for an immediate expand/edit without a round trip.
	 *  NOT persisted to local storage. */
	content?: string;
}

const KIND_BY_SHAPE: ReadonlyArray<{ test: RegExp; kind: string }> = [
	// ORDERED, AND THE ORDER IS THE RULE. A stack trace is a log; a log is not
	// necessarily a trace. Reading the more specific shape first is what stops
	// an exception dump from being filed as generic text.
	{ test: /^\s*(Traceback \(most recent call last\)|at [\w.$]+\([\w.$]+\.java:\d+\))/m, kind: 'TRACE' },
	{
		test: /(^|\n)\s*(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}|\w{3} \d{1,2} \d{2}:\d{2}:\d{2})|\[(ERROR|WARN|INFO|DEBUG)\]|^\s*(ERROR|FATAL|WARN|WARNING|INFO|DEBUG)\b/m,
		kind: 'LOG',
	},
	{ test: /^\s*(import |from |package |#include |use |const |function |class )/m, kind: 'SOURCE' },
	{ test: /^#{1,3} |\n#{1,3} /m, kind: 'REPORT' },
];

/**
 * WHAT THIS TEXT MOST LIKELY IS.
 *
 * A GUESS, AND IT IS ONLY A LABEL. The kind changes nothing about how the
 * content is treated — every material is untrusted text whatever it is called —
 * so being wrong costs nothing and being right makes the material list easier
 * to read. The Owner can override it.
 */
export function inferKind(text: string): string {
	for (const { test, kind } of KIND_BY_SHAPE) {
		if (test.test(text)) return kind;
	}
	return 'TEXT';
}

export interface ComposerProps {
	disabled: boolean;
	/** Whether a turn is currently in flight, so send is not pressed twice for
	 *  what the Owner means as one question. */
	sending: boolean;
	onSend: (input: { question: string; materials: AttachedMaterial[] }) => void;
	/** THE DRAFT THE PARENT CONFIRMED AS SENT. Only a draft that still matches
	 *  this is cleared: text typed while Scout was working is a NEW draft, and
	 *  erasing it would be the same silent loss a failed send must not cause. */
	clearDraft?: { nonce: number; question: string; materialIds: string[] };
	/** A DRAFT TO PUT BACK AFTER A SEND THAT WAS NOT ACCEPTED. The parent clears
	 *  the box when the turn is submitted (so the Owner can keep typing while
	 *  Scout works) and restores the exact draft if the submission fails. It
	 *  never overwrites a new draft the Owner has begun. */
	restoreDraft?: { nonce: number; question: string; materials: AttachedMaterial[] };
	/** THE OWNER'S INVITATION. One click gives Scout the floor for one normal,
	 *  persisted conversational turn. It is a distinct act from sending a
	 *  message: no question is typed, and the server records the click as the
	 *  trigger of a SCOUT row rather than inventing an Owner turn. */
	onScoutTurn?: () => void;
	/** Scout is composing the invited turn. The button is disabled so one
	 *  invitation cannot become two model calls. */
	scoutTurnPending?: boolean;
}

const attachOpen = signal(false);
const attachText = signal('');
const attachKind = signal('TEXT');

interface UploadProgress {
	id: string;
	name: string;
	progress: number;
}

function linesOf(text: string): number {
	return text.split('\n').length;
}

function pastedTitle(text: string): string {
	return `Pasted text — ${linesOf(text).toLocaleString()} lines`;
}

function isImageMaterial(material: {
	mediaType?: string;
	extractionStatus?: string;
}): boolean {
	return (
		material.extractionStatus === 'IMAGE' ||
		(material.mediaType?.startsWith('image/') ?? false)
	);
}


export function Composer({
	disabled,
	sending,
	onSend,
	clearDraft,
	restoreDraft,
	onScoutTurn,
	scoutTurnPending,
}: ComposerProps) {
	const [question, setQuestion] = useState('');
	const [materials, setMaterials] = useState<AttachedMaterial[]>([]);
	const [uploads, setUploads] = useState<UploadProgress[]>([]);
	const [notice, setNotice] = useState<string | null>(null);
	const [attaching, setAttaching] = useState(false);
	const [busyId, setBusyId] = useState<string | null>(null);
	const [expanded, setExpanded] = useState<{ materialId: string; text: string; note: string | null } | null>(null);
	const [editing, setEditing] = useState<{ materialId: string; text: string } | null>(null);
	const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
	const [dragging, setDragging] = useState(false);
	const textarea = useRef<HTMLTextAreaElement | null>(null);
	const fileInput = useRef<HTMLInputElement | null>(null);
	const previewUrlsRef = useRef<Record<string, string>>({});
	const questionRef = useRef(question);
	questionRef.current = question;
	const materialsRef = useRef(materials);
	materialsRef.current = materials;
	const lastClearNonce = useRef<number>(clearDraft?.nonce ?? 0);
	const lastRestoreNonce = useRef<number>(0);
	/** A SYNCHRONOUS LOCK FOR ONE SUBMISSION. `sending` is a prop and arrives
	 *  one render late; a second Enter in the same event-tick would otherwise
	 *  submit the same draft twice. Released when the parent is no longer
	 *  sending (or, for a caller that never sets `sending`, on the next tick). */
	const sendLock = useRef(false);
	const sendingRef = useRef(sending);
	sendingRef.current = sending;

	const overLong = question.length > QUESTION_MAX_CHARS;


	useEffect(() => {
		if (clearDraft === undefined || clearDraft.nonce === lastClearNonce.current) return;
		lastClearNonce.current = clearDraft.nonce;
		// CLEAR ONLY THE DRAFT THAT WAS SENT. If the Owner kept typing while
		// Scout worked — or changed the attachments — that is a new draft, and
		// the accepted turn has no claim on it.
		const sameQuestion = questionRef.current.trim() === clearDraft.question;
		const currentIds = materialsRef.current.map((material) => material.materialId);
		const sameMaterials =
			currentIds.length === clearDraft.materialIds.length &&
			currentIds.every((id, index) => id === clearDraft.materialIds[index]);
		if (!sameQuestion || !sameMaterials) return;
		setQuestion('');
		setMaterials([]);
		setExpanded(null);
		setEditing(null);
		setNotice(null);
		// THE UPLOADED FILES ARE NOT DELETED. A chip disappearing from the
		// draft is not a request to forget the Owner's document; deletion has
		// its own owner-scoped route.
	}, [clearDraft]);

	useEffect(() => {
		if (restoreDraft === undefined || restoreDraft.nonce === lastRestoreNonce.current) return;
		lastRestoreNonce.current = restoreDraft.nonce;
		// A NEW DRAFT WINS. The clear effect and this one can land in either
		// order, so restoring is allowed into an empty box OR over the exact
		// draft that failed — never over text or attachments the Owner has
		// begun since. Restoring the same draft twice loses nothing.
		const currentQuestion = questionRef.current.trim();
		const currentIds = materialsRef.current.map((material) => material.materialId);
		const failedIds = restoreDraft.materials.map((material) => material.materialId);
		const sameMaterials =
			currentIds.length === failedIds.length &&
			currentIds.every((id, index) => id === failedIds[index]);
		const sameDraft = currentQuestion === restoreDraft.question && sameMaterials;
		const empty = currentQuestion.length === 0 && currentIds.length === 0;
		if (!sameDraft && !empty) return;
		setQuestion(restoreDraft.question);
		setMaterials(restoreDraft.materials);
		setExpanded(null);
		setEditing(null);
		setNotice(null);
		requestAnimationFrame(() => textarea.current?.focus());
	}, [restoreDraft]);

	useEffect(() => {
		// THE SUBMISSION LOCK IS THE PARENT'S `sending` STATE, ONE TICK EARLY.
		if (!sending) sendLock.current = false;
	}, [sending]);

	useEffect(() => {
		// THE BOX GROWS WITH THE MESSAGE AND STOPS AT A SANE HEIGHT.
		const element = textarea.current;
		if (element === null) return;
		element.style.height = 'auto';
		element.style.height = `${Math.min(element.scrollHeight, 200)}px`;
		element.style.overflowY = element.scrollHeight > 200 ? 'auto' : 'hidden';
	}, [question]);

	useEffect(() => {
		return () => {
			for (const url of Object.values(previewUrlsRef.current)) URL.revokeObjectURL(url);
		};
	}, []);

	function rememberPreview(materialId: string, url: string): void {
		const previous = previewUrlsRef.current[materialId];
		if (previous !== undefined) URL.revokeObjectURL(previous);
		previewUrlsRef.current = { ...previewUrlsRef.current, [materialId]: url };
		setPreviewUrls({ ...previewUrlsRef.current });
	}

	function forgetPreview(materialId: string): void {
		const url = previewUrlsRef.current[materialId];
		if (url !== undefined) URL.revokeObjectURL(url);
		const next = { ...previewUrlsRef.current };
		delete next[materialId];
		previewUrlsRef.current = next;
		setPreviewUrls(next);
	}

	function addMaterial(material: AttachedMaterial): void {
		setMaterials((current) =>
			current.some((entry) => entry.materialId === material.materialId)
				? current
				: [...current, material],
		);
	}

	async function attach(content: string): Promise<void> {
		const text = content.trim();
		if (text.length === 0) {
			setNotice('There is nothing to attach.');
			return;
		}
		setAttaching(true);
		setNotice(null);
		const result = await ingestMaterial({
			content,
			kind: inferKind(text),
			title: pastedTitle(text),
		});
		setAttaching(false);
		if (!result.ok) {
			setNotice(`Scout did not take the material: ${result.error.message}`);
			return;
		}
		if (!result.data.accepted || result.data.material === undefined) {
			// THE SERVER'S OWN REASON, unedited — it names the ceiling, and a
			// paraphrase here would be a second, weaker statement of the bound.
			setNotice(result.data.reason ?? 'Scout did not take the material.');
			return;
		}
		const material = result.data.material;
		addMaterial({
			materialId: material.materialId,
			title: pastedTitle(text),
			lineCount: material.lineCount,
			byteSize: material.byteSize,
			mediaType: 'text/plain; charset=utf-8',
			origin: 'PASTE',
			content,
		});
		attachText.value = '';
		attachOpen.value = false;
		if (result.data.alreadyHeld === true) {
			setNotice('Scout already held exactly this text, so nothing was stored twice.');
		}
	}

	/** FILE THE PASTED TEXT, AND ON FAILURE PUT IT BACK. A paste that could not
	 *  be filed must not vanish because the control intercepted it; the text is
	 *  appended to the message box so the Owner still has every character. */
	async function collapsePaste(text: string): Promise<void> {
		setAttaching(true);
		setNotice(null);
		const result = await ingestMaterial({
			content: text,
			kind: inferKind(text),
			title: pastedTitle(text),
		});
		setAttaching(false);
		if (!result.ok || !result.data.accepted || result.data.material === undefined) {
			const reason = result.ok
				? (result.data.reason ?? 'Scout did not take the pasted text.')
				: result.error.message;
			setNotice(`The pasted text was left in the message box: ${reason}`);
			setQuestion((current) => (current.length === 0 ? text : `${current}\n\n${text}`));
			return;
		}
		const material = result.data.material;
		addMaterial({
			materialId: material.materialId,
			title: pastedTitle(text),
			lineCount: material.lineCount,
			byteSize: material.byteSize,
			mediaType: 'text/plain; charset=utf-8',
			origin: 'PASTE',
			content: text,
		});
		if (result.data.alreadyHeld === true) {
			setNotice('Scout already held exactly this text, so nothing was stored twice.');
		}
	}

	async function uploadFiles(files: readonly File[]): Promise<void> {
		for (const file of files) {
			const id = `upload:${file.name}:${String(Date.now())}:${Math.random().toString(36).slice(2, 8)}`;
			setUploads((current) => [...current, { id, name: file.name, progress: 0 }]);
			const result = await uploadMaterialFile(file, {
				onProgress: (progress) =>
					setUploads((current) =>
						current.map((entry) => (entry.id === id ? { ...entry, progress } : entry)),
					),
			});
			setUploads((current) => current.filter((entry) => entry.id !== id));
			if (!result.ok) {
				setNotice(`${file.name} did not upload: ${result.error.message}`);
				continue;
			}
			const data = result.data;
			if (!data.accepted || data.material === undefined) {
				setNotice(data.reason ?? `${file.name} was refused.`);
				continue;
			}
			const uploaded = data.material;
			addMaterial({
				materialId: uploaded.materialId,
				title: uploaded.fileName,
				lineCount: 0,
				byteSize: uploaded.byteSize,
				fileName: uploaded.fileName,
				mediaType: uploaded.mediaType,
				extractionStatus: uploaded.extractionStatus,
				extractionNote: uploaded.extractionNote,
				origin: 'FILE',
			});
			if (isImageMaterial({ mediaType: uploaded.mediaType, extractionStatus: uploaded.extractionStatus })) {
				rememberPreview(uploaded.materialId, URL.createObjectURL(file));
			}
			if (data.alreadyHeld === true) {
				setNotice(`Scout already held exactly ${file.name}, so nothing was stored twice.`);
			} else if (
				uploaded.extractionStatus === 'UNSUPPORTED' ||
				uploaded.extractionStatus === 'FAILED' ||
				uploaded.extractionStatus === 'IMAGE_ONLY'
			) {
				// THE FILE IS ATTACHED, BUT ITS CONTENT IS NOT READABLE. Saying so
				// now is the whole point of the extraction status; leaving the
				// Owner to discover it in the answer would be the UI accepting a
				// file the model cannot see.
				setNotice(`${file.name} is attached. ${uploaded.extractionNote}`);
			}
		}
	}

	function handleFiles(files: FileList | null): void {
		if (files === null || files.length === 0) return;
		void uploadFiles([...files]);
	}

	function handlePaste(event: ClipboardEvent): void {
		const clipboard = event.clipboardData;
		if (clipboard === null) return;
		const images = [...clipboard.items]
			.filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
			.map((item) => item.getAsFile())
			.filter((file): file is File => file !== null);
		if (images.length > 0) {
			event.preventDefault();
			void uploadFiles(images);
			return;
		}
		const text = clipboard.getData('text/plain');
		if (text.length === 0) return;
		if (text.length > 2_000_000) {
			// A PASTE OVER THE MATERIAL CEILING IS LEFT AS TEXT. Truncating it or
			// dropping it would be the exact failure this control exists to
			// avoid; the server will name the ceiling if it is sent.
			setNotice(
				`That paste is ${text.length.toLocaleString()} characters, over this build's 2,000,000-character material ceiling. It was left in the message box rather than dropped or shortened.`,
			);
			return;
		}
		if (
			text.length >= PASTE_COLLAPSE_CHARS ||
			linesOf(text) >= PASTE_COLLAPSE_LINES
		) {
			event.preventDefault();
			void collapsePaste(text);
		}
	}

	async function showMaterial(material: AttachedMaterial): Promise<void> {
		if (expanded?.materialId === material.materialId) {
			setExpanded(null);
			return;
		}
		if (material.content !== undefined) {
			setExpanded({
				materialId: material.materialId,
				text: material.content,
				note:
					material.content.length > PREVIEW_CHARS
						? `Showing the first ${PREVIEW_CHARS.toLocaleString()} of ${material.content.length.toLocaleString()} characters. The whole document is stored; Download returns the original.`
						: null,
			});
			return;
		}
		setBusyId(material.materialId);
		setNotice(null);
		const result = await readMaterialText(material.materialId);
		setBusyId(null);
		if (!result.ok) {
			setNotice(`The material could not be read back: ${result.error.message}`);
			return;
		}
		setExpanded({
			materialId: material.materialId,
			text: result.data.slice(0, PREVIEW_CHARS),
			note:
				result.data.length > PREVIEW_CHARS
					? `Showing the first ${PREVIEW_CHARS.toLocaleString()} of ${result.data.length.toLocaleString()} characters. The whole document is stored; Download returns the original.`
					: null,
		});
	}

	async function startEdit(material: AttachedMaterial): Promise<void> {
		if (material.content !== undefined) {
			setEditing({ materialId: material.materialId, text: material.content });
			return;
		}
		setBusyId(material.materialId);
		const result = await readMaterialText(material.materialId);
		setBusyId(null);
		if (!result.ok) {
			setNotice(`The material could not be read back: ${result.error.message}`);
			return;
		}
		setEditing({ materialId: material.materialId, text: result.data });
	}

	async function saveEdit(): Promise<void> {
		if (editing === null) return;
		const text = editing.text;
		if (text.trim().length === 0) {
			setNotice('An edited material cannot be empty. Remove it instead.');
			return;
		}
		setAttaching(true);
		setNotice(null);
		const result = await ingestMaterial({
			content: text,
			kind: inferKind(text),
			title: pastedTitle(text),
		});
		setAttaching(false);
		if (!result.ok || !result.data.accepted || result.data.material === undefined) {
			const reason = result.ok
				? (result.data.reason ?? 'Scout did not take the edited material.')
				: result.error.message;
			setNotice(`The edit was not saved: ${reason}`);
			return;
		}
		const material = result.data.material;
		const replaced = editing.materialId;
		setMaterials((current) =>
			current.map((entry) =>
				entry.materialId === replaced
					? {
							materialId: material.materialId,
							title: pastedTitle(text),
							lineCount: material.lineCount,
							byteSize: material.byteSize,
							mediaType: 'text/plain; charset=utf-8',
							origin: 'PASTE' as const,
							content: text,
						}
					: entry,
			),
		);
		forgetPreview(replaced);
		setEditing(null);
		setExpanded(null);
	}

	async function downloadMaterial(material: AttachedMaterial): Promise<void> {
		const result = await loadMaterialBlob(material.materialId);
		if (!result.ok) {
			setNotice(`The original could not be downloaded: ${result.error.message}`);
			return;
		}
		const url = URL.createObjectURL(result.blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = material.fileName ?? material.title;
		anchor.rel = 'noopener';
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
		setTimeout(() => URL.revokeObjectURL(url), 60_000);
	}

	function send(): void {
		// THE SAME DRAFT IS ONE SUBMISSION. The disabled button is painted after
		// this event; a rapid second Enter would otherwise reach the parent while
		// its first request is still being accepted.
		if (sendLock.current) return;
		const trimmed = question.trim();
		if (trimmed.length === 0 && materials.length === 0) return;
		if (uploads.length > 0) {
			setNotice('Wait for the files to finish uploading.');
			return;
		}
		if (materials.length > MATERIAL_MAX_REFS_PER_TURN) {
			setNotice(
				`A turn may read at most ${MATERIAL_MAX_REFS_PER_TURN} materials; remove ${(materials.length - MATERIAL_MAX_REFS_PER_TURN).toLocaleString()} before sending. Nothing has been discarded.`,
			);
			return;
		}
		if (overLong) {
			setNotice(
				`That is ${question.length.toLocaleString()} characters and a question may be ${QUESTION_MAX_CHARS.toLocaleString()}. Attach it as material and ask about it in a sentence — nothing is shortened either way.`,
			);
			return;
		}
		setNotice(null);
		sendLock.current = true;
		try {
			onSend({ question: trimmed, materials });
		} catch (error) {
			sendLock.current = false;
			throw error;
		}
		// A caller that never flips `sending` (a specimen, or a parent that
		// refused the turn synchronously) must not leave the box locked.
		setTimeout(() => {
			if (!sendingRef.current) sendLock.current = false;
		}, 0);
	}

	const canSend = !disabled && !sending && !overLong && uploads.length === 0;

	return (
		<form
			class={`composer${dragging ? ' is-dragging' : ''}`}
			onSubmit={(event) => {
				event.preventDefault();
				if (canSend) send();
			}}
			onDragOver={(event) => {
				event.preventDefault();
				setDragging(true);
			}}
			onDragLeave={(event) => {
				const next = event.relatedTarget as Node | null;
				if (next !== null && event.currentTarget.contains(next)) return;
				setDragging(false);
			}}
			onDrop={(event) => {
				event.preventDefault();
				setDragging(false);
				const files = event.dataTransfer?.files;
				if (files !== undefined && files.length > 0) {
					handleFiles(files);
					return;
				}
				const text = event.dataTransfer?.getData('text/plain') ?? '';
				if (text.length > 0) {
					if (text.length >= PASTE_COLLAPSE_CHARS || linesOf(text) >= PASTE_COLLAPSE_LINES) {
						void collapsePaste(text);
					} else {
						setQuestion((current) => (current.length === 0 ? text : `${current}\n${text}`));
					}
				}
			}}
		>
			<input
				ref={fileInput}
				type="file"
				multiple
				class="visually-hidden"
				aria-label="Attach files"
				accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.txt,.md,.markdown,.rtf,.csv,.tsv,.json,.jsonl,.xml,.html,.htm,.svg,.css,.js,.mjs,.cjs,.jsx,.ts,.tsx,.py,.sql,.log,.yaml,.yml,.toml,.ini,.cfg,.conf,.sh,.java,.c,.h,.cpp,.hpp,.rs,.go,.rb,.php,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff,.avif,.heic"
				onChange={(event) => {
					handleFiles((event.target as HTMLInputElement).files);
					// THE SAME FILE MAY BE CHOSEN AGAIN AFTER A REFUSAL. Clearing the
					// value is what makes the picker fire for an identical selection.
					(event.target as HTMLInputElement).value = '';
				}}
			/>

			{(materials.length > 0 || uploads.length > 0) && (
				<ul class="attachments" aria-label="Attached material">
					{materials.map((material) => {
						const imageUrl = previewUrls[material.materialId];
						const problematic =
							material.extractionStatus === 'UNSUPPORTED' ||
							material.extractionStatus === 'FAILED' ||
							material.extractionStatus === 'IMAGE_ONLY';
						return (
							<li
								key={material.materialId}
								class={`attachment-chip${problematic ? ' is-warning' : ''}`}
								data-material-id={material.materialId}
								data-origin={material.origin ?? 'FILE'}
							>
								<div class="attachment-chip-head">
									{imageUrl === undefined ? (
										<span class="attachment-kind" aria-hidden="true">
											{material.origin === 'PASTE' ? 'TEXT' : (material.extractionStatus ?? 'FILE')}
										</span>
									) : (
										<img class="attachment-thumb" src={imageUrl} alt={`Preview of ${material.title}`} />
									)}
									<span class="attachment-title">{material.title}</span>
									<span class="muted small">
										{[
											material.mediaType?.split(';')[0],
											formatBytes(material.byteSize),
											material.lineCount > 0
												? `${material.lineCount.toLocaleString()} lines`
												: null,
										]
											.filter((part): part is string => part !== null && part !== undefined && part.length > 0)
											.join(' · ')}
									</span>
								</div>
								{problematic && material.extractionNote !== undefined && (
									<p class="attachment-warning small">{material.extractionNote}</p>
								)}
								<div class="attachment-chip-actions">
									{!isImageMaterial(material) && (
										<button
											type="button"
											class="button ghost small"
											onClick={() => void showMaterial(material)}
											disabled={busyId === material.materialId}
											aria-expanded={expanded?.materialId === material.materialId}
										>
											{busyId === material.materialId
												? 'Reading…'
												: expanded?.materialId === material.materialId
													? 'Hide'
													: 'Show'}
										</button>
									)}
									{material.origin === 'PASTE' && (
										<button
											type="button"
											class="button ghost small"
											onClick={() => void startEdit(material)}
											disabled={busyId === material.materialId}
										>
											Edit
										</button>
									)}
									{material.origin === 'FILE' && (
										<button
											type="button"
											class="button ghost small"
											onClick={() => void downloadMaterial(material)}
										>
											Download
										</button>
									)}
									<button
										type="button"
										class="button ghost small"
										onClick={() => {
											if (expanded?.materialId === material.materialId) setExpanded(null);
											forgetPreview(material.materialId);
											setMaterials((current) =>
												current.filter((entry) => entry.materialId !== material.materialId),
											);
										}}
									>
										Remove
									</button>
								</div>
								{expanded?.materialId === material.materialId && (
									<div class="attachment-preview-wrap">
										{expanded.note !== null && (
											<p class="muted small">{expanded.note}</p>
										)}
										<pre class="attachment-preview">{expanded.text}</pre>
										<button
											type="button"
											class="button ghost small"
											onClick={() => setExpanded(null)}
										>
											Close preview
										</button>
									</div>
								)}
								{editing?.materialId === material.materialId && (
									<div class="attachment-edit">
										<textarea
											class="composer-input"
											rows={8}
											value={editing.text}
											aria-label="Edit attached material"
											onInput={(event) =>
												setEditing((current) =>
													current === null
														? current
														: {
																...current,
																text: (event.target as HTMLTextAreaElement).value,
															},
												)
											}
										/>
										<div class="attach-actions">
											<button
												type="button"
												class="button"
												disabled={attaching}
												onClick={() => void saveEdit()}
											>
												{attaching ? 'Saving…' : 'Save changes'}
											</button>
											<button
												type="button"
												class="button ghost"
												onClick={() => setEditing(null)}
											>
												Cancel
											</button>
										</div>
									</div>
								)}
							</li>
						);
					})}
					{uploads.map((upload) => (
						<li key={upload.id} class="attachment-chip is-uploading" data-origin="UPLOAD">
							<div class="attachment-chip-head">
								<span class="attachment-kind" aria-hidden="true">
									FILE
								</span>
								<span class="attachment-title">{upload.name}</span>
								<span class="muted small">
									{Math.round(upload.progress * 100)}%
								</span>
							</div>
							<progress max={1} value={upload.progress} aria-label={`Uploading ${upload.name}`} />
						</li>
					))}
				</ul>
			)}

			<div class="composer-entry">
				<span class="composer-prompt" aria-hidden="true">
					&gt;
				</span>
				<textarea
					ref={textarea}
					class="composer-input"
					value={question}
					rows={1}
					placeholder="Ask Scout anything, or paste what you are looking at…"
					aria-label="Your message to Scout"
					disabled={disabled}
					onInput={(event) => {
						const value = (event.target as HTMLTextAreaElement).value;
						// THE REF UPDATES BEFORE THE RENDER. A clear that lands
						// between this keystroke and the next render must see the
						// Owner's new text, not the draft that was just sent.
						questionRef.current = value;
						setQuestion(value);
					}}
					onPaste={handlePaste}
					onKeyDown={(event) => {
						// ENTER SENDS, SHIFT+ENTER BREAKS THE LINE. A chat where the
						// obvious key does not send is a chat that feels broken; a
						// chat where it always sends cannot hold a paragraph.
						if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
							event.preventDefault();
							if (canSend) send();
						}
					}}
				/>
			</div>

			{overLong && (
				<p class="composer-overflow small" role="status">
					{question.length.toLocaleString()} characters — longer than a question. Attach it instead;
					Scout will keep all of it and read the part that matters.
				</p>
			)}

			{attachOpen.value && (
				<div class="attach-area">
					<textarea
						class="composer-input"
						rows={6}
						value={attachText.value}
						placeholder="Paste the log, trace, report or source excerpt here…"
						aria-label="Material to attach"
						onInput={(event) => {
							const value = (event.target as HTMLTextAreaElement).value;
							attachText.value = value;
							attachKind.value = inferKind(value);
						}}
					/>
					<p class="muted small">
						Scout will file this whole, as{' '}
						<select
							value={attachKind.value}
							aria-label="What this material is"
							onChange={(event) =>
								(attachKind.value = (event.target as HTMLSelectElement).value)
							}
						>
							{['TEXT', 'LOG', 'TRACE', 'REPORT', 'SOURCE', 'DOCUMENT'].map((kind) => (
								<option key={kind} value={kind}>
									{kind}
								</option>
							))}
						</select>{' '}
						— a label that changes nothing about how it is treated, because every material is
						content to read and never an instruction to follow.
					</p>
					<div class="attach-actions">
						<button
							type="button"
							class="button"
							disabled={attaching || attachText.value.trim().length === 0}
							onClick={() => void attach(attachText.value)}
						>
							{attaching ? 'Filing…' : 'Attach'}
						</button>
						<button
							type="button"
							class="button ghost"
							onClick={() => {
								attachOpen.value = false;
								attachText.value = '';
							}}
						>
							Close
						</button>
					</div>
				</div>
			)}

			{notice !== null && (
				<p class="composer-notice small" role="alert">
					{notice}
				</p>
			)}

			<div class="composer-actions">
				<button
					type="button"
					class="button ghost"
					onClick={() => fileInput.current?.click()}
					disabled={disabled || sending}
				>
					Attach files
				</button>
				<button
					type="button"
					class="button ghost"
					onClick={() => {
						attachOpen.value = !attachOpen.value;
					}}
					aria-expanded={attachOpen.value}
				>
					Attach long text
				</button>
				{question.length > QUESTION_MAX_CHARS * 0.8 && !overLong && (
					<span class="muted small">
						{(QUESTION_MAX_CHARS - question.length).toLocaleString()} characters left
					</span>
				)}
				{onScoutTurn !== undefined && (
					<button
						type="button"
						class="button ghost scout-cerita"
						disabled={disabled || sending || scoutTurnPending === true}
						onClick={onScoutTurn}
						aria-label="Scout, cerita — invite Scout to speak first"
						title="Give Scout the floor for one conversational turn"
					>
						{scoutTurnPending === true ? 'Scout is thinking…' : 'Scout, cerita'}
					</button>
				)}
				<button type="submit" class="button" disabled={!canSend}>
					{sending ? 'Scout is working…' : 'Send'}
				</button>
			</div>
		</form>
	);
}
